/**
 * ThinDataDetectionMessage — AI chat callout rendered when the backend's propose_panels
 * response includes a thin-preview flag (totalRowCount < 20 across all domains).
 *
 * Brand guide §24. Mockup: designs/ai-dashboard-platform-v3/2026-04-23-29-thin-preview-detection-messaging-v3.html
 *
 * Structure (per brand guide §24):
 *  1. Plain prose opener stating the actual row count.
 *  2. Amber callout block listing specific panels that are unreliable at this volume.
 *  3. Plain prose bridge sentence.
 *  4. Three suggestion chips: "Widen date range" / "Add more sessions" / "Continue anyway".
 *
 * Rules:
 *  - Uses "limited data" not "thin" in user-facing copy.
 *  - Not an error state — AI avatar uses default bg-accent (not amber/red).
 *  - Does not block the user from using the dashboard — informational only.
 *  - "Add a previous season for year-over-year comparison" chip must always appear when
 *    the current filter set has only one season selected (enforced here for creation flow).
 *  - When `isSending` is true, send-bound chips ("Widen date range" / "Add more sessions")
 *    are disabled so they cannot silently drop their payload while the previous send is
 *    still in-flight. "Continue anyway" is always enabled because it does not depend on
 *    the chat send queue.
 */

import React from 'react'
import { AlertTriangle, Calendar, ListPlus, ArrowRight, Sparkles } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch } from '@/redux'
import { Button } from '@/components/ui/button'
import {
  dashboardCreationActions,
  selectThinPreviewWarning,
} from '@/redux/slice/dashboardCreationSlice'
import { shouldShortCircuitSuggestion } from './thinPreviewGuards'

// ─── Suggestion chip data ─────────────────────────────────────────────────────

interface SuggestionChip {
  id: 'widen_date_range' | 'add_more_sessions' | 'continue_anyway'
  label: string
  icon: React.ReactNode
}

const SUGGESTION_CHIPS: SuggestionChip[] = [
  {
    id: 'widen_date_range',
    label: 'Widen date range',
    icon: <Calendar size={14} className="flex-shrink-0" aria-hidden="true" />,
  },
  {
    id: 'add_more_sessions',
    label: 'Add a previous season for year-over-year comparison',
    icon: <ListPlus size={14} className="flex-shrink-0" aria-hidden="true" />,
  },
  {
    id: 'continue_anyway',
    label: 'Continue anyway',
    icon: <ArrowRight size={14} className="flex-shrink-0" aria-hidden="true" />,
  },
]

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ThinDataDetectionMessageProps {
  rowCount: number
  unreliablePanels: string[]
  /** Called when a suggestion chip is clicked. The chip id is passed. */
  onSuggestionClick: (suggestionId: SuggestionChip['id']) => void
  /**
   * When true, send-bound chips are disabled to prevent the ChatPanel from
   * silently dropping the suggestion message during an in-flight send.
   * "Continue anyway" remains enabled because it does not send a message.
   */
  isSending?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ThinDataDetectionMessage renders an AI-bubble-style message with:
 *  - An amber callout block listing unreliable panels.
 *  - Three suggestion chips for recovery actions.
 *
 * Rendered inside ChatPanel when `selectThinPreviewWarning` is non-null.
 */
export const ThinDataDetectionMessage: React.FC<ThinDataDetectionMessageProps> = ({
  rowCount,
  unreliablePanels,
  onSuggestionClick,
  isSending = false,
}) => {
  const recordLabel = rowCount === 1 ? 'enrollment record' : 'enrollment records'

  return (
    <div
      className="flex gap-3 items-start"
      data-testid="thin-data-detection-message"
      // role="status" implies aria-live="polite" — no need to duplicate the attribute.
      role="status"
    >
      {/* AI avatar — default accent bg per §24 rule "AI avatar uses default bg-accent".
          Uses the same Sparkles lucide icon as ChatPanel's AI avatar for visual consistency. */}
      <div
        className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0"
        aria-hidden="true"
      >
        <Sparkles size={14} className="text-accent-foreground" aria-hidden="true" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-5 py-4 text-sm text-primary max-w-full shadow-[0_1px_4px_rgba(15,23,42,0.06)]">

          {/* Prose opener — uses generic date window phrase so copy stays accurate
              regardless of which filter window the user has selected (CR2). */}
          <p className="leading-relaxed mb-3">
            Your current date range only had{' '}
            <strong>{rowCount} {recordLabel}</strong> — these panel proposals are
            best guesses from limited data. Full season data will give us more
            once it loads.
          </p>

          {/* Amber callout block — §24 */}
          <div
            className="my-3 p-3 bg-warning-foreground border border-warning/30 rounded-xl"
            role="note"
            aria-label="Limited data warning"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-warning flex-shrink-0" aria-hidden="true" />
              <p className="text-xs font-bold text-warning">
                Limited data — panels may not be meaningful yet
              </p>
            </div>

            {unreliablePanels.length > 0 && (
              <>
                <p className="text-xs text-warning/80 leading-relaxed mb-2">
                  These panels are potentially unreliable with the current data volume:
                </p>
                <ul
                  className="text-xs text-warning/80 space-y-1 ml-4 list-disc"
                  aria-label="Potentially unreliable panels"
                >
                  {unreliablePanels.map(panel => (
                    <li key={panel}>{panel}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Bridge sentence */}
          <p className="text-sm text-secondary leading-relaxed mb-3">
            You can adjust your filters to capture more data, or continue with what we have.
          </p>

          {/* Suggestion chips — §24.
              Rendered as Shadcn Button (variant="outline") so focus-visible rings,
              disabled styles, and keyboard affordances come from the design system. */}
          <div
            className="flex flex-col gap-2"
            role="group"
            aria-label="Suggested actions"
          >
            {SUGGESTION_CHIPS.map(chip => {
              // Send-bound chips are disabled while a previous send is in flight,
              // so the ChatPanel guard does not silently drop the suggestion payload.
              // "Continue anyway" is always enabled — it only clears the callout.
              const isSendBound = chip.id !== 'continue_anyway'
              const disabled = isSending && isSendBound
              return (
                <Button
                  key={chip.id}
                  type="button"
                  variant="outline"
                  disabled={disabled}
                  aria-disabled={disabled}
                  className="flex items-start justify-start gap-2.5 w-full text-left px-3 py-2.5 h-auto bg-card border border-border rounded-xl text-sm text-secondary font-medium hover:border-accent hover:text-accent hover:bg-card transition-all duration-150 focus-visible:ring-focusRing disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => onSuggestionClick(chip.id)}
                  aria-label={chip.label}
                >
                  {chip.icon}
                  {chip.label}
                </Button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Connected variant (reads from Redux, dispatches clearThinPreviewWarning) ──

/**
 * ConnectedThinDataDetectionMessage reads thinPreviewWarning from Redux and
 * renders ThinDataDetectionMessage when set. Clears the warning after the
 * user clicks "Continue anyway". Wires suggestion chip clicks to the chat
 * input (caller provides onSendMessage callback).
 */
export interface ConnectedThinDataDetectionMessageProps {
  /** Called when the user clicks a suggestion chip that sends a chat message. */
  onSendSuggestion: (messageText: string) => void
  /**
   * Mirrors the ChatPanel's in-flight-send flag. Send-bound chips are disabled
   * while true so the callout doesn't vanish with no message delivered (the
   * ChatPanel.sendChatMessage guard silently drops messages while isSending).
   */
  isSending?: boolean
}

export const ConnectedThinDataDetectionMessage: React.FC<ConnectedThinDataDetectionMessageProps> = ({
  onSendSuggestion,
  isSending = false,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const thinPreviewWarning = useSelector(selectThinPreviewWarning)

  if (!thinPreviewWarning) return null

  const handleSuggestionClick = (id: SuggestionChip['id']) => {
    // Belt-and-braces guard mirroring the disabled prop on the chip:
    // if a send-bound chip is clicked while isSending is true (e.g. via keyboard
    // before the disabled attribute applies), do nothing — DON'T clear the
    // warning and DON'T call onSendSuggestion. The chip becomes live again
    // once the in-flight send completes.
    if (shouldShortCircuitSuggestion(isSending, id)) return

    if (id === 'continue_anyway') {
      dispatch(dashboardCreationActions.clearThinPreviewWarning())
      return
    }

    if (id === 'widen_date_range') {
      onSendSuggestion('Widen date range')
      dispatch(dashboardCreationActions.clearThinPreviewWarning())
      return
    }

    if (id === 'add_more_sessions') {
      onSendSuggestion('Add a previous season for year-over-year comparison')
      dispatch(dashboardCreationActions.clearThinPreviewWarning())
      return
    }
  }

  return (
    <ThinDataDetectionMessage
      rowCount={thinPreviewWarning.rowCount}
      unreliablePanels={thinPreviewWarning.unreliablePanels}
      onSuggestionClick={handleSuggestionClick}
      isSending={isSending}
    />
  )
}
